import * as d3 from "d3";
import {
  formatValue,
  pluralize,
} from "@/components/VivoPlots/functions/utils/valueUtils";
import { addTooltip } from "@/components/VivoPlots/functions/utils/tooltipFunctions";
import { getShapeInformation } from "@/components/VivoPlots/functions/drawPlotData/utils/getShapeInfo";
import { isSubjectData } from "@/components/VivoPlots/functions/utils/isSubjectData";
import { palettes } from "./utils/palletes";
import { tooltipPosition } from "../utils/tickTooltip";
import {
  getApplicableCriteria,
  isSelectionApplied,
  type PlotElementSelection,
} from "@/components/VivoPlots/functions/utils/plotSelection";

const ARROW_STEPS: Record<string, number> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
};

const selectableBarNodes = (plotClassName: string): SVGPathElement[] =>
  d3
    .select(`svg.${plotClassName}`)
    .selectAll<SVGPathElement, unknown>("path.bar-selectable")
    .nodes();

/**
 * Bars are a roving tabindex, not one tab stop each: a plot with forty
 * categories would otherwise put a hundred-odd sequential stops between the
 * user and the next control on the page.
 */
const setBarTabStop = (nodes: SVGPathElement[], activeIndex: number) => {
  nodes.forEach((node, index) => {
    node.setAttribute("tabindex", index === activeIndex ? "0" : "-1");
  });
};

const moveBarFocus = (
  plotClassName: string,
  current: SVGPathElement,
  step: number,
) => {
  const nodes = selectableBarNodes(plotClassName);
  const currentIndex = nodes.indexOf(current);
  if (currentIndex === -1 || nodes.length === 0) return;

  const nextIndex = (currentIndex + step + nodes.length) % nodes.length;
  setBarTabStop(nodes, nextIndex);
  nodes[nextIndex].focus();
};

export function drawBars(
  plotClassName: string,
  series: any[],
  scales: any[],
  valueVar: string,
  groupLegendPostscript: string | null | undefined,
  selectedIdValueData: any[],
  plotConfig?: any,
) {
  const isHorizontal = plotConfig?.orientation === "horizontal";
  const colorScale = scales?.find((scale) => scale?.type === "fill");
  const totalScale = scales?.find((scale) => scale?.type === "total");
  const categoricalScale = scales?.find(
    (scale) => scale?.type === "categorical",
  );
  const subgroupScale = scales?.find((scale) => scale?.type === "subgroup");

  const tooltip = addTooltip(plotClassName);
  const colorDomain = colorScale?.scale?.domain();

  const orderedSeries = series
    ?.map((serie) => {
      const orderedStack = serie?.stacks?.sort((a: any, b: any) => {
        return colorDomain.indexOf(a.key) - colorDomain.indexOf(b.key);
      });
      return {
        ...serie,
        stacks: orderedStack,
      };
    })
    ?.sort((a, b) => a.index - b.index);

  const { shapePosition: barPosition, shapeWidth: barWidth } =
    getShapeInformation(subgroupScale, categoricalScale);

  // Activating a bar re-filters the screen, which redraws every bar and
  // destroys the focused node. Remember whether focus was inside this plot so
  // it can be handed back rather than dumped on the document body.
  const hadFocusInPlot = Boolean(
    document.activeElement?.matches(":focus-visible") &&
    d3
      .select<SVGSVGElement, unknown>(`svg.${plotClassName}`)
      .node()
      ?.contains(document.activeElement),
  );

  const onElementSelect = plotConfig?.onElementSelect;
  const filterableColumns = new Set<string>(
    plotConfig?.filterableColumns ?? [],
  );
  const appliedFilters = plotConfig?.appliedFilters ?? {};

  // Each bar's selection state is derived once and cached on the datum: the
  // draw chain reads it from a dozen attribute callbacks, and recomputing it
  // per callback made every redraw allocate ten arrays per bar.
  const barDatumCache = new Map<any, any>();

  const toBarDatum = (d: any, stackKey: any, subgroupKey: any) => {
    const cacheKey = `${stackKey}\u0000${subgroupKey}`;
    const cachedForEntry = barDatumCache.get(d);
    if (cachedForEntry?.cacheKey === cacheKey) return cachedForEntry;

    const groupKey = d.data[categoricalScale.dataMapping];
    const selection: PlotElementSelection = {
      criteria: [
        { column: categoricalScale?.dataMapping, value: groupKey },
        { column: colorScale?.dataMapping, value: stackKey },
        { column: subgroupScale?.dataMapping, value: subgroupKey },
      ].filter((criterion) => Boolean(criterion.column)),
    };
    // One source of truth for "could this be filtered on", shared with the
    // code that actually applies it, so affordance and behaviour cannot drift.
    const applicableCriteria = getApplicableCriteria(
      selection,
      filterableColumns,
    );
    const isSelected =
      applicableCriteria.length > 0 &&
      isSelectionApplied(appliedFilters, selection, filterableColumns);

    const datum = {
      cacheKey,
      stack: d,
      stackKey,
      subgroupKey,
      groupKey,
      value: d[valueVar],
      data: d.data,
      selection,
      // Only the criteria that will actually be applied are announced, so the
      // label never promises a filter the click does not deliver.
      selectionLabel: applicableCriteria
        .map((criterion) => criterion.value)
        .join(", "),
      isSelectable: Boolean(onElementSelect) && applicableCriteria.length > 0,
      isSelected,
    };

    barDatumCache.set(d, datum);
    return datum;
  };

  const hasSelectedBar = (orderedSeries ?? []).some((serie: any) =>
    (serie.stacks ?? []).some((D: any) =>
      D.filter((d: any) => d[0] !== d[1]).some(
        (d: any) => toBarDatum(d, D.key, serie.key).isSelected,
      ),
    ),
  );

  //remove all bars
  d3.select(`svg.${plotClassName}`).selectAll("g.bars").remove();

  orderedSeries?.forEach((serie) => {
    d3.select(`svg.${plotClassName}`)
      .append("g")
      .classed("bars", true)
      .selectAll()
      .data(serie.stacks)
      .join("g")
      .classed("series", true)
      .selectAll("path")
      .data((D: any): any[] =>
        D.filter((d: any) => d[0] !== d[1]).map((d: any) =>
          toBarDatum(d, D.key, serie.key),
        ),
      )
      .join("path")
      .attr("class", (d) => {
        return d.data[categoricalScale.dataMapping];
      })
      .attr("rx", 8)
      .attr("fill", (d) => {
        return d.stackKey && colorScale
          ? colorScale.scale(d.stackKey)
          : (palettes as any)[plotConfig?.palette || "Blue"]?.fill[1];
      })
      /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
      .attr("opacity", (d, i, nodes) => {
        if (selectedIdValueData && selectedIdValueData.length > 0) {
          const isSubject = isSubjectData(
            d,
            selectedIdValueData,
            categoricalScale,
            subgroupScale,
            colorScale,
          );

          if (isSubject) {
            return 1;
          }
          return 0.2;
        }
        if (hasSelectedBar) {
          return d.isSelected ? 1 : 0.35;
        }
        return 1;
      })

      .attr("stroke", (d) => {
        return d.stackKey && colorScale
          ? colorScale.strokeScale(d.stackKey)
          : (palettes as any)[plotConfig?.palette || "Blue"]?.stroke[1];
      })
      /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
      .attr("d", (d, a, b) => {
        const allStacks = serie.stacks;

        const currentStackData = allStacks.find(
          (stack: any) => stack.key === d.stackKey,
        );

        const barIndex = currentStackData?.findIndex((stackCategory: any) => {
          return (
            stackCategory.data[categoricalScale.dataMapping] ===
            d.data[categoricalScale.dataMapping]
          );
        });

        let isTopStack = false;

        if (barIndex !== undefined && barIndex !== -1) {
          const barValues = allStacks.map(
            (stackCategory: any) => stackCategory[barIndex][1],
          );
          const maxBarValue = Math.max(...barValues);

          isTopStack = d.stack[1] === maxBarValue;
        }

        if (isHorizontal) {
          const x = totalScale.scale(d.stack[0]);
          const y = barPosition(d);
          const width =
            totalScale.scale(d.stack[1]) - totalScale.scale(d.stack[0]);
          const height = barWidth();
          return roundedRightRect(x, y, width, height, isTopStack ? 8 : 0);
        }
        const x = barPosition(d);
        const y = totalScale.scale(d.stack[1]);
        const width = barWidth();
        const height =
          totalScale.scale(d.stack[0]) - totalScale.scale(d.stack[1]);
        return roundedTopRect(
          x,
          y,
          width,
          height,
          isTopStack ? 8 : 0, // Only round the top if it's the top stack
        );
      })
      /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
      .attr("width", (d) => barWidth())
      .attr("cursor", (d) => (d.isSelectable ? "pointer" : null))
      // SVG shapes are not focusable by default.
      .attr("tabindex", (d) => (d.isSelectable ? -1 : null))
      .attr("role", (d) => (d.isSelectable ? "button" : null))
      .attr("aria-label", (d) =>
        d.isSelectable
          ? `${d.isSelected ? "Remove filter" : "Filter by"} ${d.selectionLabel}`
          : null,
      )
      .attr("aria-pressed", (d) =>
        d.isSelectable ? String(d.isSelected) : null,
      )
      .classed("bar-selectable", (d) => d.isSelectable)
      .classed("bar-selected", (d) => d.isSelected)
      .attr("stroke-width", (d) => (d.isSelected ? 2.5 : null))
      .on("click", function (event, d) {
        if (!d.isSelectable) return;
        event.stopPropagation();
        onElementSelect(d.selection);
      })
      .on("keydown", function (event, d) {
        if (!d.isSelectable) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onElementSelect(d.selection);
          return;
        }
        const step = ARROW_STEPS[event.key];
        if (!step) return;
        event.preventDefault();
        moveBarFocus(plotClassName, this as SVGPathElement, step);
      })
      .on("focus", function (event, d) {
        if (!d.isSelectable) return;
        // :focus-visible so a mouse click does not leave a focus ring behind.
        if (!(this as SVGPathElement).matches(":focus-visible")) return;
        d3.select(this).attr("stroke-width", d.isSelected ? 3.5 : 2.5);
      })
      .on("blur", function (event, d) {
        d3.select(this).attr("stroke-width", d.isSelected ? 2.5 : null);
      })
      .on("mouseover", function (event, d) {
        if (d.isSelectable) {
          d3.select(this).attr("stroke-width", d.isSelected ? 3.5 : 2.5);
        }
        const isPercentage = plotConfig?.barplotPercentage;
        const stackValue =
          Math.round(d.data[d.subgroupKey][d.stackKey] * 100) / 100;

        const barTotal = Object.keys(d.data[d.subgroupKey]).reduce(
          (acc, curr) => {
            return acc + d.data[d.subgroupKey][curr];
          },
          0,
        );

        tooltip.transition().duration(50).style("opacity", 0.9);
        const container = d3.select(`.plot-container.${plotClassName}`);
        const pos = tooltipPosition(
          event,
          container as unknown as d3.Selection<HTMLElement, unknown, any, any>,
          tooltip as unknown as d3.Selection<HTMLDivElement, unknown, any, any>,
        );
        const left = pos?.left ?? 0;
        const top = pos?.top ?? 0;

        const categoryAxisLabel = isHorizontal ? "Y" : "X";

        tooltip
          .html(
            `<p>${
              `${categoryAxisLabel}: ${d.data[categoricalScale.dataMapping]}${
                groupLegendPostscript
                  ? pluralize(
                      d.data[categoricalScale.dataMapping],
                      groupLegendPostscript,
                    )
                  : ""
              }<br/>` +
              `${d.stackKey !== "none" ? `Color: ${d.stackKey}<br/>` : ""}` +
              `${
                d.subgroupKey !== "none" ? `Group: ${d.subgroupKey}<br/>` : ""
              }` +
              `Value: ${formatValue(stackValue)}${isPercentage ? "%" : ""}` +
              `${d.stackKey !== "none" ? `<br/>Total: ${barTotal}` : ""}`
            }</p>`,
          )

          .style("left", left + "px")
          .style("top", top + "px");
      })
      .on("mousemove", function (event) {
        const container = d3.select(`.plot-container.${plotClassName}`);
        const pos = tooltipPosition(
          event,
          container as unknown as d3.Selection<HTMLElement, unknown, any, any>,
          tooltip as unknown as d3.Selection<HTMLDivElement, unknown, any, any>,
        );
        const left = pos?.left ?? 0;
        const top = pos?.top ?? 0;
        tooltip.style("left", left + "px").style("top", top + "px");
      })
      .style("z", "999")

      .on("mouseout", function (event, d) {
        d3.select(this).attr("stroke-width", d.isSelected ? 2.5 : null);
        tooltip.transition().duration(50).style("opacity", 0);
      });
  });

  const selectableNodes = selectableBarNodes(plotClassName);
  if (selectableNodes.length > 0) {
    const selectedIndex = selectableNodes.findIndex((node) =>
      node.classList.contains("bar-selected"),
    );
    const tabStopIndex = selectedIndex === -1 ? 0 : selectedIndex;
    setBarTabStop(selectableNodes, tabStopIndex);
    if (hadFocusInPlot) selectableNodes[tabStopIndex].focus();
  }
}

// x,y = top-left of the bar; height extends downward
function roundedTopRect(
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  const r = Math.max(0, Math.min(radius, width / 2, height));
  return `
      M ${x},${y + height}
      V ${y + r}
      A ${r},${r} 0 0 1 ${x + r},${y}
      H ${x + width - r}
      A ${r},${r} 0 0 1 ${x + width},${y + r}
      V ${y + height}
      Z
    `;
}

function roundedRightRect(
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  const r = Math.max(0, Math.min(radius, width / 2, height / 2));
  return `
    M ${x},${y}
    H ${x + width - r}
    A ${r},${r} 0 0 1 ${x + width},${y + r}
    V ${y + height - r}
    A ${r},${r} 0 0 1 ${x + width - r},${y + height}
    H ${x}
    V ${y}
    Z
  `;
}
